import { Button, Col, Flex, Form, PaginationProps, Popover, Row } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { PencilFill, Plus, ThreeDotsVertical, Trash, XSquare } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import {
  useCreateUserRoles,
  useDeleteUserRoles,
  useEditUserRoles,
  useGetUserRoles,
} from '../../../api/hooks/useUserQuery';
import { ActionMenu } from '../../../components/common/ActionMenu';
import ModelComponent from '../../../components/common/ModelComponent';
import TableComponent from '../../../components/common/TableComponent';
import { FormButton } from '../../../components/form/FormBasic';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { Configuration } from '../../../constants/configs';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../../constants/regex';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { getServerErrorMsg } from '../../../helpers/errorHandler';
import { customizeRequiredMark, renderField, resetFormFields } from '../../../helpers/formHelper';
import { getColorFormattedText } from '../../../helpers/utilityHelper';
import { UserRole, UserRoleCreateRequest, UserRoleEditRequest } from '../../../types/user';
import { usePermissions } from '../../../hooks/usePermissions';
import { PermissionEnum, PermissionGuardEmbedTypeEnum } from '../../../enums/PermissionEnum';
import PermissionGuard from '../../../guard/permissionGuard';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';

type UserRoleTableRow = UserRole & { key: string };

const UserRolesTable = () => {
  const { t } = useTranslation(['userManagement', 'common']);
  const { errorNotification, successNotification } = useNotificationContext();
  const [isShowRegister, setIsShowRegister] = useState(false);
  const [isShowEditUser, setIsShowEditUser] = useState(false);
  const [isShowDeleteConfirmation, setIsShowDeleteConformation] = useState(false);
  const [form] = Form.useForm();
  const userRoleCreateMutation = useCreateUserRoles();
  const userRoleEditMutation = useEditUserRoles();
  const userRoleGetMutation = useGetUserRoles();
  const userRoleDeleteMutation = useDeleteUserRoles();
  const [userRoleTableRow, setUserRoleTableRow] = useState<UserRoleTableRow[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const selectedUserRole = useRef({} as any);
  const { hasPermission } = usePermissions();
  const isHasRoleEdit = hasPermission([PermissionEnum.USER_ROLE, PermissionEnum.MANAGE_USER_ROLE]);
  const [loading, setLoading] = useState(false);
  const { isOnline } = useIsOnlineContext();

  useEffect(() => {
    getUserRoles();
  }, [currentPage, pageSize]);

  const handleSubmit = (_: any) => {
    const userSubmitData: UserRoleCreateRequest = {
      name: form.getFieldValue('name'),
      description: form.getFieldValue('description'),
      shortName: form.getFieldValue('shortName'),
    };

    userRoleCreateMutation.mutate(userSubmitData, {
      onSuccess: () => {
        setIsShowRegister(false);
        form.resetFields();
        successNotification(t('userRoleCreated'));
        getUserRoles();
      },
      onError: (response) => {
        const errorMsg = getServerErrorMsg(response);
        errorNotification(errorMsg);
      },
    });
  };

  const showUserRegister = () => {
    setIsShowRegister(true);
  };
  const handleCancelRegister = () => {
    resetFormFields(form, Configuration.userTypeRegisterForms);
    setIsShowRegister(false);
  };

  const handleCancelEdit = () => {
    resetFormFields(form, Configuration.userTypeRegisterForms);
    setIsShowEditUser(false);
  };

  const getUserRoles = () => {
    setLoading(true);
    userRoleGetMutation.mutateAsync(
      {
        page: currentPage,
        size: pageSize,
        sort: {
          key: 'id',
          order: 'DESC',
        },
      },
      {
        onSuccess: (data) => {
          const rolesList: UserRoleTableRow[] = data.data?.data?.data?.map((roles: any) => {
            return {
              id: roles.id,
              name: t(`${roles.name}`),
              shortName: t(`${roles.shortName}`),
              showName: t(`${roles.showName}`),
              description: t(`${roles.description}`),
              roleType: roles.roleType,
            };
          });
          setUserRoleTableRow(rolesList);
          setTotalRecords(data.data?.data?.total || 0);
          setLoading(false);
        },
        onError(error) {
          console.log('error ', error);
          setLoading(false);
        },
      }
    );
  };

  const showUserEdit = (user: UserRole) => {
    selectedUserRole.current = user;

    Configuration.userTypeRegisterForms.forEach((formItem: any) => {
      const key = formItem.fieldId as keyof UserRole;
      form.setFieldValue(formItem.fieldId, user[key]);
    });
    setIsShowEditUser(true);
  };

  const handleEditForm = (_: any) => {
    const userSubmitData: UserRoleEditRequest = {
      name: form.getFieldValue('name'),
      description: form.getFieldValue('description'),
      shortName: form.getFieldValue('shortName'),
      id: selectedUserRole.current.id,
    };

    userRoleEditMutation.mutate(userSubmitData, {
      onSuccess: () => {
        successNotification(t('userRoleUpdated'));
        getUserRoles();
        form.resetFields();
        setIsShowEditUser(false);
      },
      onError: (response) => {
        const errorMsg = getServerErrorMsg(response);
        errorNotification(errorMsg);
      },
      onSettled: () => {},
    });
  };

  const dynamicMenuItems = (record: UserRole) => {
    const items = [];
    items.push({
      text: t('edit'),
      icon: <PencilFill size={16} className="text-primary-blue-icon" />,
      click: () => {
        showUserEdit(record);
      },
    });

    items.push({
      text: t('delete'),
      icon: <Trash size={16} className="text-primary-blue-icon" />,
      click: () => {
        selectedUserRole.current = record;
        setIsShowDeleteConformation(true);
      },
    });
    return items;
  };

  const columns = [
    {
      title: t('userRoleId'),
      dataIndex: 'id',
      key: 'id',
      with: 100,
    },
    {
      title: t('userRole'),
      dataIndex: 'name',
      key: 'name',
      with: 200,
    },
    {
      title: t('roleCode'),
      dataIndex: 'shortName',
      key: 'shortName',
      with: 100,
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      with: 100,
    },
    ...(isHasRoleEdit
      ? [
          {
            title: t('action'),
            dataIndex: 'action',
            key: 'action',
            with: 200,
            align: 'center',
            render: (_: any, record: UserRole) => {
              const menuItems = dynamicMenuItems(record);

              if (menuItems.length === 0) {
                return (
                  <Row justify="center" style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                    <ThreeDotsVertical size={15} />
                  </Row>
                );
              }

              return (
                <Popover
                  content={<ActionMenu menuItems={menuItems} />}
                  className="cursor-pointer"
                  placement="bottom"
                  trigger="hover"
                  overlayStyle={{ zIndex: 800 }}
                >
                  <Row justify="center">
                    <ThreeDotsVertical size={15} />
                  </Row>
                </Popover>
              );
            },
          },
        ]
      : []),
  ];

  const getRegisterForm = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        requiredMark={customizeRequiredMark}
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={[16, 16]}>
          {Configuration.userTypeRegisterForms.map((originalField: any) => {
            const field = { ...originalField };
            if (field.fieldId === 'name') {
              field.rules = [
                { required: true, message: t('fieldIsRequired', { field: t('roleName') }) },
                {
                  validator: (_: any, value: string) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('fieldSpaceValidation', { field: t('roleName') }));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value) return Promise.resolve();
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value)
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value || value.trim() === '') return Promise.resolve();
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 1) {
                      return Promise.reject(t('fieldMinValidation', { field: t('roleName'), min: 1 }));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value || value.trim() === '') return Promise.resolve();
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('fieldMaxValidation', { field: t('roleName'), max: 100 }));
                    }
                    return Promise.resolve();
                  },
                },  
              ];
            } else if (field.fieldId === 'shortName') {
              field.rules = [
                { required: true, message: t('fieldIsRequired', { field: t('roleCode') }) },
                {
                  validator: (_: any, value: string) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('fieldSpaceValidation', { field: t('roleCode') }));
                    }
                    return Promise.resolve();
                  },
                },                
                {
                  validator: (_: any, value: string) => {
                    if (!value) return Promise.resolve();
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value)
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value || value.trim() === '') return Promise.resolve();
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 1) {
                      return Promise.reject(t('fieldMinValidation', { field: t('roleCode'), min: 1 }));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value || value.trim() === '') return Promise.resolve();
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('fieldMaxValidation', { field: t('roleCode'), max: 100 }));
                    }
                    return Promise.resolve();
                  },
                },
              ];
            } else if (field.fieldId === 'description') {
              field.rules = [
                { required: true, message: t('fieldIsRequired', { field: t('description') }) },
                {
                  validator: (_: any, value: string) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('fieldSpaceValidation', { field: t('description') }));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value) return Promise.resolve();
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value)
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value || value.trim() === '') return Promise.resolve();
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 1) {
                      return Promise.reject(t('fieldMinValidation', { field: t('description'), min: 1 }));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: string) => {
                    if (!value || value.trim() === '') return Promise.resolve();
                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('fieldMaxValidation', { field: t('description'), max: 100 }));
                    }
                    return Promise.resolve();
                  },
                },
              ];
            }
            return (
              <Col span={24 / field.rowSize} key={field.fieldId}>
                {renderField(form, field, t)}
              </Col>
            );
          })}
        </Row>

        <Form.Item>
          <Flex className="justify-end gap-3">
            <Button onClick={handleCancelRegister} className="btn-primary-border">
              {t('cancel')}
            </Button>
            <FormButton
              type="submit"
              label={t('Register')}
              isLoading={userRoleCreateMutation.isPending}
            />
          </Flex>
        </Form.Item>
        {/* <ReactFormBuilder />, */}
      </Form>
    );
  };

  const getEditForm = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        requiredMark={customizeRequiredMark}
        onFinish={handleEditForm}
        autoComplete="off"
      >
        <Row gutter={[16, 16]}>
          {Configuration.userTypeRegisterForms
            .filter((formItem: any) => formItem.fieldId !== 'email')
            .map((originalField: any) => {
              const field = { ...originalField };
              if (field.fieldId === 'name') {
                field.rules = [
                  { required: true, message: t('fieldIsRequired', { field: t('roleName') }) },
                  {
                    validator: (_: any, value: string) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('fieldSpaceValidation', { field: t('roleName') }));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value) return Promise.resolve();
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value)
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value || value.trim() === '') return Promise.resolve();
                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('fieldMinValidation', { field: t('roleName'), min: 1 }));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value || value.trim() === '') return Promise.resolve();
                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('fieldMaxValidation', { field: t('roleName'), max: 100 }));
                      }
                      return Promise.resolve();
                    },
                  },
                ];
              } else if (field.fieldId === 'shortName') {
                field.rules = [
                  { required: true, message: t('fieldIsRequired', { field: t('roleCode') }) },
                  {
                    validator: (_: any, value: string) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('fieldSpaceValidation', { field: t('roleCode') }));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value) return Promise.resolve();
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value)
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value || value.trim() === '') return Promise.resolve();
                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('fieldMinValidation', { field: t('roleCode'), min: 1 }));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value || value.trim() === '') return Promise.resolve();
                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('fieldMaxValidation', { field: t('roleCode'), max: 100 }));
                      }
                      return Promise.resolve();
                    },
                  },
                ];
              } else if (field.fieldId === 'description') {
                field.rules = [
                  { required: true, message: t('fieldIsRequired', { field: t('description') }) },
                  {
                    validator: (_: any, value: string) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('fieldSpaceValidation', { field: t('description') }));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value) return Promise.resolve();
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value)
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value || value.trim() === '') return Promise.resolve();
                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('fieldMinValidation', { field: t('description'), min: 1 }));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value || value.trim() === '') return Promise.resolve();
                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('fieldMaxValidation', { field: t('description'), max: 100 }));
                      }
                      return Promise.resolve();
                    },
                  },
                ];
              }
              return (
                <Col span={24 / field.rowSize} key={field.fieldId}>
                  {renderField(form, field, t)}
                </Col>
              );
            })}
        </Row>
        <Form.Item>
          <Flex className="justify-end gap-3">
            <Button onClick={handleCancelEdit} className="btn-primary-border">
              {t('cancel')}
            </Button>
            <FormButton
              type="submit"
              label={t('save')}
              isLoading={userRoleEditMutation.isPending}
            />
          </Flex>
        </Form.Item>
      </Form>
    );
  };

  const userRoleDeleteConfirmationHeaderContent = () => {
    return (
      <label
        className="text-primary-font-gray my-6 px-12 text-center text-xl font-bold"
        /* dangerouslySetInnerHTML={{
          __html: getColorFormattedText(
            t('deleteRoleConfirmation'),
            t('cancel'),
            'text-primary-font-red'
          ),
        }} */
      >{ t('deleteRoleConfirmation')}</label>
    );
  };

  const onCancelRequestCanceled = () => {
    setIsShowDeleteConformation(false);
  };

  const onDeleteRole = () => {
    userRoleDeleteMutation.mutate(
      { id: selectedUserRole.current.id, versionId: 1.0 },
      {
        onSuccess() {
          successNotification(t('messageUserRoleDeleted'));
          getUserRoles();
          setIsShowDeleteConformation(false);
        },
        onError(error) {
          const msg = getServerErrorMsg(error);
          errorNotification(msg);
        },
      }
    );
  };

  const onPaginationChanged: PaginationProps['onChange'] = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  return (
    <Row>
      <Flex className="w-full" justify="end">
        <PermissionGuard
          permission={[PermissionEnum.USER_ROLE, PermissionEnum.MANAGE_USER_ROLE]}
          embedType={PermissionGuardEmbedTypeEnum.COMPONENT}
        >
          <PrimarySquareBtn handleOnClick={showUserRegister} disabled={!isOnline}>
            <div className="flex flex-row items-center gap-2">
              <Plus className="h-6 w-6 text-white" /> {t('addRole')}
            </div>
          </PrimarySquareBtn>
          {/* <FormButton
            onClick={showUserRegister}
            icon={<Plus></Plus>}
            label={''}
            isIconOnly
            type={'button'}
          ></FormButton> */}
        </PermissionGuard>
      </Flex>
      <div className="w-full overflow-x-auto">
        <TableComponent
          tableData={userRoleTableRow}
          pageSize={pageSize}
          currentPage={currentPage}
          totalItems={totalRecords}
          onPaginationChanged={onPaginationChanged}
          columns={columns}
          showHeader
          loading={loading}
        ></TableComponent>
      </div>
      <ModelComponent
        open={isShowRegister}
        title={t('registerNewType')}
        footer={null}
        handleCancel={handleCancelRegister}
        content={getRegisterForm()}
      />

      <ModelComponent
        open={isShowEditUser}
        title={t('editType')}
        footer={null}
        handleCancel={handleCancelEdit}
        content={getEditForm()}
      />
      <ConfirmModelComponent
        isOpen={isShowDeleteConfirmation}
        headerContent={userRoleDeleteConfirmationHeaderContent()}
        content={t('deleteRoleConfirmationMessage')}
        onClickedCancel={onCancelRequestCanceled}
        footerAction={{
          confimation: {
            label: t('deleteNow'),
            onClick: onDeleteRole,
          },
          cancel: {
            label: t('back'),
            onClick: onCancelRequestCanceled,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
      ></ConfirmModelComponent>
    </Row>
  );
};

export default UserRolesTable;
